import { observer } from 'mobx-react';
import { Key, useEffect, useMemo, useRef, useState } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { Checkbox, Form, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ActionType } from '@ant-design/pro-components';
import dayjs from 'dayjs';
import { HiSearch } from 'react-icons/hi';

import PageWrapper from 'pages/Components/PageWrapper';
import CustomButton from 'base/ui/components/Button';
import BoxContainer from 'base/ui/components/BoxContainer';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { SearchBox } from 'pages/Components/SearchBox';
import { PAGE_SIZE_DEFAULT, SYSTEM_TITLE } from 'utils/constants';
import { usePermission } from 'hooks/usePermission';
import { checkPermissionWithId } from 'utils/permissionHelper';
import {
  commonPresenter,
  commonStore,
  wasteHandoverPlanPresenter,
  wasteHandoverPlanStore,
} from 'stores/root_store';
import { getAccountId, getFullName, getRole, getUserName } from 'utils/localStorageHelper';
import { saveSearchParams, getSearchParams, clearSearchParams } from 'utils/searchParamsHelper';
import { WasteHandoverPlanModel } from 'services/Main/wasteHandoverPlan_services';
import { ROUTES } from 'base/routes/routes';

// Import components và utilities
import { initSearch, initSearchFilters } from './components/constants';
import { formatDateOnly } from './components/utils/dateHelpers';
import { buildExportPayload, buildSendEmailPayload } from './components/utils/payloadHelpers';
import { useColumnFilters } from './components/TableFilters/useColumnFilters';
import { useTableColumns } from './components/TableColumns';
import { WasteHandoverPlanSearchForm } from './components/SearchForm';
import { ActionButtons } from './components/ActionButtons';
import SendMailModal from './components/SendMailModal';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import XacNhanImportModal from './components/XacNhanImportModal';


// màn hình Kế hoạch bàn giao chất thải/phế liệu
const createWasteHandoverPlan = () => {
  return observer(() => {
    const { t } = useTranslation();
    document.title = t('waste-handover-plan.title') + ' - ' + SYSTEM_TITLE;
    usePermission('INDEX');

    // ==================== PERMISSIONS ====================
    const isCreate = checkPermissionWithId('CREATE');
    const isEdit = checkPermissionWithId('EDIT');
    const isDelete = checkPermissionWithId('DELETE');
    const isExport = checkPermissionWithId('EXPORT');
    const isImport = checkPermissionWithId('IMPORT');
    const isExportTemplate = checkPermissionWithId('EXPORT_TEMPLATE');
    const isCreateVote = checkPermissionWithId('CREATE_VOTE');
    const isSendEmail = checkPermissionWithId('GUIMAIL');

    // ==================== STATE ====================
    const [form] = Form.useForm();
    const [openDrawer, setOpenDrawer] = useState(false);
    const [dataSearch, setDataSearch] = useState<any>({ ...initSearch, filters: { ...initSearchFilters } });
    const [sourceData, setSourceData] = useState<WasteHandoverPlanModel[]>([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [disableDeliveryDate, setDisableDeliveryDate] = useState(false);
    const [openSendMail, setOpenSendMail] = useState(false);

    const [openXacNhanImport, setOpenXacNhanImport] = useState(false);

    // Loading states cho các action
    const [exportLoading, setExportLoading] = useState(false);
    const [exportTemplateLoading, setExportTemplateLoading] = useState(false);
    const [exportEditTemplateLoading, setExportEditTemplateLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const [sendEmailLoading, setSendEmailLoading] = useState(false);
    const [selectionConstraint, setSelectionConstraint] = useState<{
      date: string;
      vendorNumber: string;
      wasteGroup: string;
    } | null>(null);
    const navigate = useNavigate();

    const tableRef = useRef<ActionType>();

    const [copyRowKeys, setCopyRowKeys] = useState<Key[]>([]);
    const [isCopyMode, setIsCopyMode] = useState(false);

    // ==================== HOOKS ====================
    // Hook để tạo column filters
    const { getColumnSearchProps } = useColumnFilters(sourceData);

    // ==================== INITIALIZATION ====================
    // Load dropdown data và set ngày mặc định (Today) khi component mount
    useEffect(() => {
      const initDropdowns = async () => {
        try {
          await Promise.allSettled([
            commonPresenter.getListCommonDate(commonStore, 'CommonDate2'),
            commonPresenter.getSeleOrgByAccountId(commonStore, getAccountId()),
          ]);

          form.setFieldsValue({
            ...(getRole() === "NCC_CT" && { vendorNumber: getUserName(), vendorName: getFullName() }),
          })

          // Kiểm tra xem có saved search params không
          const savedParams = getSearchParams('wasteHandoverPlan');

          if (savedParams?.filters) {
            // Nếu có saved params, load lại trạng thái search trước đó
            console.log('Loading saved search params:', savedParams);

            let paramsToUse: any = { ...savedParams };

            // Nếu có deliveryDatePreset (khác 'Custom'), gọi lại API để lấy ngày hiện tại cho preset đó
            // Điều này đảm bảo "Today" luôn là hôm nay, "ThisMonth" là tháng này, v.v.
            if (savedParams.filters.deliveryDatePreset && savedParams.filters.deliveryDatePreset !== 'Custom') {
              await commonPresenter.getDateByCommonDate(commonStore, savedParams.filters.deliveryDatePreset);
              if (commonStore.commonDate.fromDate) {
                paramsToUse = {
                  ...paramsToUse,
                  filters: {
                    ...paramsToUse.filters,
                    deliveryDateFrom: commonStore.commonDate.fromDate,
                    deliveryDateTo: commonStore.commonDate.toDate,
                  },
                };
                // Lưu lại params với ngày đã cập nhật
                saveSearchParams('wasteHandoverPlan', paramsToUse);
              }
            }

            setDataSearch(paramsToUse);

            // Set form values từ saved params - Convert string dates back to dayjs để hiển thị trong form
            form.setFieldsValue({
              ...paramsToUse.filters,
              deliveryDateFrom: paramsToUse.filters.deliveryDateFrom
                ? dayjs(paramsToUse.filters.deliveryDateFrom)
                : undefined,
              deliveryDateTo: paramsToUse.filters.deliveryDateTo
                ? dayjs(paramsToUse.filters.deliveryDateTo)
                : undefined,
            });

            // Set disable state cho delivery date
            setDisableDeliveryDate(paramsToUse.filters.deliveryDatePreset && paramsToUse.filters.deliveryDatePreset !== 'Custom');

            // Load data với saved filters
            await loadData(paramsToUse.filters);
            setIsInitialized(true);
          } else {
            // Nếu không có saved params, lấy ngày "Today" và set vào dataSearch
            await commonPresenter.getDateByCommonDate(commonStore, 'Today');
            if (commonStore.commonDate.fromDate && commonStore.commonDate.toDate) {
              const todayFrom = dayjs(commonStore.commonDate.fromDate, 'YYYY-MM-DD');
              const todayTo = dayjs(commonStore.commonDate.toDate, 'YYYY-MM-DD');

              const defaultFilters = {
                ...initSearchFilters,
                deliveryDateFrom: todayFrom,
                deliveryDateTo: todayTo,
                deliveryDatePreset: 'Today',
                ...(getRole() === "NCC_CT" && { vendorNumber: getUserName(), vendorName: getFullName() }),
              };

              const defaultSearch = {
                ...initSearch,
                filters: defaultFilters,
              };

              setDataSearch(defaultSearch);

              // Set vào form
              form.setFieldsValue({
                deliveryDateFrom: todayFrom,
                deliveryDateTo: todayTo,
                deliveryDatePreset: 'Today',
              });
              setDisableDeliveryDate(true);

              // Lưu params mặc định
              saveSearchParams('wasteHandoverPlan', defaultSearch);

              // Gọi loadData với giá trị mặc định
              await loadData(defaultFilters);
              setIsInitialized(true);
            } else {
              // Nếu không lấy được ngày Today, vẫn set initialized để loadData có thể chạy
              setIsInitialized(true);
            }
          }
        } catch (error) {
          console.error('Error loading dropdown data:', error);
        }
      };
      initDropdowns();
    }, []);

    // ==================== HANDLERS ====================
    /**
     * Hiển thị drawer tìm kiếm
     */
    const showDrawer = () => {
      setOpenDrawer(true);
    };

    /**
     * Đóng drawer tìm kiếm
     */
    const onCloseDrawer = () => {
      setOpenDrawer(false);
    };

    /**
     * Hiển thị modal xác nhận xóa
     */
    const showDeleteConfirm = (record: WasteHandoverPlanModel) => {
      Modal.confirm({
        title: `${t('waste-handover-plan.delete-title')} "${record.productCode} - ${record.materialName}"`,
        icon: <ExclamationCircleFilled />,
        content: t('waste-handover-plan.delete-content'),
        okText: 'Hủy kế hoạch',
        okType: 'danger',
        cancelText: 'Quay lại',
        className: 'custom__modalconfirm--delete',
        async onOk() {
          try {
            const response = await wasteHandoverPlanPresenter.deleteWasteHandoverPlan(record.wasteHandoverPlanId);
            if (response.isSuccess) {
              message.success(response.message || t('waste-handover-plan.delete-success'));
              // Reload lại data với filters hiện tại
              await loadData(dataSearch.filters);
              // Reload lại table
              tableRef.current?.reload();
            } else {
              message.error(response.message || 'Có lỗi xảy ra khi xóa kế hoạch.');
            }
          } catch (error) {
            console.error('Error deleting waste handover plan:', error);
            message.error('Có lỗi xảy ra khi xóa kế hoạch.');
          }
        },
        onCancel() { },
      });
    };

    // Hook để tạo table columns (phải đặt sau showDeleteConfirm)
    const defaultColumn = useTableColumns({
      getColumnSearchProps,
      isCreate,
      isEdit,
      isDelete,
      onDelete: showDeleteConfirm,
      selectionConstraint,
    });


    /**
     * Xử lý khi thay đổi preset ngày giao hàng
     */
    const handleDeliveryPresetChange = async (value: string) => {
      if (value && value !== 'Custom') {
        setDisableDeliveryDate(true);
        await commonPresenter.getDateByCommonDate(commonStore, value);
        if (commonStore.commonDate.fromDate) {
          form.setFieldsValue({
            deliveryDateFrom: dayjs(commonStore.commonDate.fromDate, 'YYYY-MM-DD'),
            deliveryDateTo: dayjs(commonStore.commonDate.toDate, 'YYYY-MM-DD'),
          });
        }
      } else {
        setDisableDeliveryDate(false);
        form.setFieldsValue({
          deliveryDateFrom: undefined,
          deliveryDateTo: undefined,
        });
      }
    };

    /**
     * Reset filters về giá trị mặc định
     */
    const handleResetFilters = async () => {
      form.resetFields();
      setDisableDeliveryDate(false);

      // Set lại giá trị mặc định cho deliveryDatePreset và load ngày từ "Today"
      let defaultFilters: any = { ...initSearchFilters };

      // Set saleOrgCode nếu có
      if (commonStore.commonStore.length) {
        const defaultSaleOrgCode = commonStore.commonStore[0].key;
        form.setFieldValue('saleOrgCode', defaultSaleOrgCode);
        defaultFilters.saleOrgCode = defaultSaleOrgCode;
      }

      form.setFieldValue('deliveryDatePreset', 'Today');
      defaultFilters.deliveryDatePreset = 'Today';

      // Load ngày từ preset "Today"
      await commonPresenter.getDateByCommonDate(commonStore, 'Today');
      if (commonStore.commonDate.fromDate) {
        const todayFrom = dayjs(commonStore.commonDate.fromDate, 'YYYY-MM-DD');
        const todayTo = dayjs(commonStore.commonDate.toDate, 'YYYY-MM-DD');
        form.setFieldsValue({
          deliveryDateFrom: todayFrom,
          deliveryDateTo: todayTo,
        });
        // Cập nhật defaultFilters với giá trị ngày
        defaultFilters.deliveryDateFrom = todayFrom;
        defaultFilters.deliveryDateTo = todayTo;
        setDisableDeliveryDate(true);
      }

      const newDataSearch = {
        ...initSearch,
        filters: defaultFilters,
        paging: { pageIndex: 1, pageSize: PAGE_SIZE_DEFAULT },
      };

      setDataSearch(newDataSearch);

      // Xóa saved search params khi reset
      clearSearchParams('wasteHandoverPlan');

      // Lưu lại params mặc định
      saveSearchParams('wasteHandoverPlan', newDataSearch);

      loadData(defaultFilters);
    };

    /**
     * Xử lý tìm kiếm
     */
    const handleSearch = (values: any) => {
      setSourceData([]);

      const newDataSearch = {
        paging: {
          pageIndex: 1,
          pageSize: PAGE_SIZE_DEFAULT,
        },
        filters: values,
      };

      setDataSearch(newDataSearch);

      // Lưu search params vào localStorage
      saveSearchParams('wasteHandoverPlan', newDataSearch);

      loadData(values);
      onCloseDrawer(); // Đóng drawer khi form submit thành công
    };

    // ==================== DATA LOADING ====================
    /**
     * Load dữ liệu từ API
     */
    const loadData = async (searchValues?: any) => {
      setTableLoading(true);
      try {
        const filters = searchValues ?? dataSearch.filters ?? {};
        // Load tất cả data để filter (giống purchaseOrder)
        const searchPayload = {
          paging: {
            pageIndex: 1, // Luôn bắt đầu từ page 1 để lấy tất cả data
            pageSize: 1000000000, // Lấy tất cả data để filter
          },
          saleOrgCode: filters.saleOrgCode,
          wasteGroupingContractIds: filters.wasteGroupingContractIds,
          productCode: filters.productCode,
          productName: filters.productName,
          vendorNumber: filters.vendorNumber,
          vendorName: filters.vendorName,
          storageLocation: filters.storageLocation,
          handoverDocumentNumber: filters.handoverDocumentNumber,
          createByIds: Array.isArray(filters.createBy) && filters.createBy.length > 0 ? filters.createBy : undefined,
          deliveryDateFrom: formatDateOnly(filters.deliveryDateFrom),
          deliveryDateTo: formatDateOnly(filters.deliveryDateTo),
          actived: filters.actived,
        };

        await wasteHandoverPlanPresenter.searchWasteHandoverPlans(
          wasteHandoverPlanStore,
          searchPayload
        );

        if (wasteHandoverPlanStore.searchWasteHandoverPlanResponse.isSuccess) {
          const apiData = wasteHandoverPlanStore.searchWasteHandoverPlanResponse.data;
          const mappedData: WasteHandoverPlanModel[] = apiData.map((item) => {
            // Xử lý materialName - nếu không có thì lấy từ productName
            let materialName = item.materialName;
            if (!materialName && item.productName) {
              materialName = typeof item.productName === 'string'
                ? item.productName
                : (item.productName as any)?.value || (item.productName as any)?.label || '';
            }

            return {
              ...item,
              wasteHandoverPlanId: item.wasteHandoverPlanId,
              deliveryDate: item.deliveryDate || '',
              wasteHandoverPlanCode: item.wasteHandoverPlanCode || '',
              wasteGroupingEnvironmentPermitName: item.wasteGroupingEnvironmentPermitName || '',
              productCode: item.productCode || '',
              materialName: materialName || '',
              vendorNumber: item.vendorNumber || '',
              vendorName: item.vendorName || '',
              storageLocation: item.storageLocation || '',
              storageLocationName: item.storageLocationName || '',
              handoverDocumentNumber: item.handoverDocumentNumber || '',
              saleOrgCode: item.saleOrgCode || '',
              saleOrgName: item.saleOrgName || '',
              note: item.note || '',
              actived: item.actived,
              createTime: item.createTime || '',
              createByName: item.createByName || '',
              productType: item.productType || '',
              wasteGroup: (item as any).wasteGroup || item.wasteGroupingContractName || '',
              isRegistered: (item as any).isRegistered,
              isSentEmail: (item as any).isSentEmail,
            };
          });

          setSourceData(mappedData);
          setDataSearch((prev: any) => ({
            ...prev,
            filters: searchValues || prev.filters,
            paging: {
              pageIndex: 1,
              pageSize: prev.paging?.pageSize || PAGE_SIZE_DEFAULT,
            },
          }));
        } else {
          setSourceData([]);
          message.error(wasteHandoverPlanStore.searchWasteHandoverPlanResponse.message);
        }
      } catch (error) {
        console.error('Error loading data:', error);
        message.error(t('waste-handover-plan.load-data-error'));
      } finally {
        setTableLoading(false);
      }
    };


    const toggleCopyKey = (id: Key, checked: boolean) => {
      setCopyRowKeys((prev) => {
        if (checked) return prev.includes(id) ? prev : [...prev, id];
        return prev.filter((k) => k !== id);
      });
    };

    const copyColumn = useMemo(() => {
      // select-all trong trang hiện tại (sourceData đang là toàn bộ data)
      const selectableKeys = sourceData.map((r) => r.wasteHandoverPlanId as Key);

      const allChecked =
        selectableKeys.length > 0 && selectableKeys.every((k) => copyRowKeys.includes(k));
      const indeterminate =
        selectableKeys.some((k) => copyRowKeys.includes(k)) && !allChecked;

      return {
        title: (
          <div className='flex items-center justify-center'>
            <Checkbox
              className="copy-mode-checkbox"
              indeterminate={indeterminate}
              checked={allChecked}
              onChange={(e: CheckboxChangeEvent) => {
                const checked = e.target.checked;
                setCopyRowKeys((prev) => {
                  if (checked) {
                    // add tất cả key có thể chọn
                    const merged = new Set([...prev, ...selectableKeys]);
                    return Array.from(merged);
                  }
                  // bỏ chọn tất cả key trong nhóm selectableKeys
                  return prev.filter((k) => !selectableKeys.includes(k));
                });
              }}
            />
            <div className="ml-1 font-bold text-xs">Copy</div>
          </div>
        ),
        dataIndex: '__copy__',
        key: '__copy__',
        width: 60,
        fixed: 'left' as const,
        align: 'center' as const,
        render: (_: any, record: WasteHandoverPlanModel) => {
          const id = record.wasteHandoverPlanId as Key;

          return (
            <div className="min-w-[40px] flex justify-center">
              <Checkbox
                className="copy-mode-checkbox"
                checked={copyRowKeys.includes(id)}
                onChange={(e) => toggleCopyKey(id, e.target.checked)}
              />
            </div>
          );
        },
      };
    }, [sourceData, copyRowKeys]);

    const handleEnterCopyMode = () => {
      setIsCopyMode(true);
      setCopyRowKeys([]);
      setSelectedRowKeys([]);
      setSelectionConstraint(null);
    };

    const handleCancelCopyMode = () => {
      setIsCopyMode(false);
      setCopyRowKeys([]);
      setSelectionConstraint(null);
    };

    const handleConfirmCopyMode = () => {
      if (!copyRowKeys.length) {
        message.warning('Vui lòng chọn kế hoạch cần sao chép');
        return;
      }

      const selectedPlans = sourceData.filter((item) =>
        copyRowKeys.includes(item.wasteHandoverPlanId as Key)
      );

      navigate(ROUTES.WASTE_HANDOVER_PLAN.COPY.LINK, {
        state: { dataDetail: selectedPlans },
      });
    };

    const columnsWithCopy = useMemo(() => {
      return [copyColumn, ...defaultColumn];
    }, [copyColumn, defaultColumn]);

    const [isInitialized, setIsInitialized] = useState(false);

    // ==================== EXPORT/IMPORT HANDLERS ====================
    /**
     * Xử lý export danh sách
     */
    const handleExportList = async () => {
      setExportLoading(true);
      try {
        const exportPayload = buildExportPayload(dataSearch.filters ?? {});
        await wasteHandoverPlanPresenter.exportWasteHandoverPlanList(exportPayload);
      } finally {
        setExportLoading(false);
      }
    };

    /**
     * Xử lý export template tạo mới
     */
    const handleExportTemplate = async () => {
      setExportTemplateLoading(true);
      try {
        await wasteHandoverPlanPresenter.exportWasteHandoverPlanCreateTemplate();
      } finally {
        setExportTemplateLoading(false);
      }
    };

    /**
     * Xử lý khi click vào menu item trong dropdown export template
     */
    const handleExportTemplateMenuClick = async ({ key }: { key: string }) => {
      if (key === 'edit-template') {
        setExportEditTemplateLoading(true);
        try {
          const exportPayload = buildExportPayload(dataSearch.filters ?? {});
          await wasteHandoverPlanPresenter.exportWasteHandoverPlanEditTemplate(exportPayload);
        } finally {
          setExportEditTemplateLoading(false);
        }
      }
    };

    /**
     * Xử lý khi import file
     */
    const handleImportChange = async (info: any) => {
      if (info.file?.status === 'uploading') {
        setImportLoading(true);
      }
      if (info.file?.status === 'done') {
        setImportLoading(false);
        message.success(info.file.response?.message || t('message.success'));

        // Reset về trạng thái ban đầu (không giữ bộ lọc)
        form.resetFields();
        setDisableDeliveryDate(false);

        // Set lại giá trị mặc định cho deliveryDatePreset và load ngày từ "Today"
        let defaultFilters: any = { ...initSearchFilters };

        // Set saleOrgCode nếu có
        if (commonStore.commonStore.length) {
          const defaultSaleOrgCode = commonStore.commonStore[0].key;
          form.setFieldValue('saleOrgCode', defaultSaleOrgCode);
          defaultFilters.saleOrgCode = defaultSaleOrgCode;
        }

        form.setFieldValue('deliveryDatePreset', 'Today');
        defaultFilters.deliveryDatePreset = 'Today';

        // Load ngày từ preset "Today"
        await commonPresenter.getDateByCommonDate(commonStore, 'Today');
        if (commonStore.commonDate.fromDate) {
          const todayFrom = dayjs(commonStore.commonDate.fromDate, 'YYYY-MM-DD');
          const todayTo = dayjs(commonStore.commonDate.toDate, 'YYYY-MM-DD');
          form.setFieldsValue({
            deliveryDateFrom: todayFrom,
            deliveryDateTo: todayTo,
          });
          defaultFilters.deliveryDateFrom = todayFrom;
          defaultFilters.deliveryDateTo = todayTo;
          setDisableDeliveryDate(true);
        }

        // Reset dataSearch về trạng thái ban đầu
        setDataSearch({
          ...initSearch,
          filters: defaultFilters,
        });

        // Load lại data với filters mặc định
        await loadData(defaultFilters);
        // Reload lại table
        tableRef.current?.reload();
      }
      if (info.file.status === 'error') {
        setImportLoading(false);
        message.error(info.file.response?.message || t('message.error'));
      }
    };

    /**
     * Xử lý gửi email
     */
    const handleSendEmail = () => {
      setOpenSendMail(true);
    };

    /**
     * Xử lý tạo phiếu
     */
    const handleCreateVoucher = () => {
      if (!selectedRowKeys.length) {
        message.warning(t('message.required-select'));
        return;
      }
      const selectedRows = sourceData.filter((item) =>
        selectedRowKeys.includes(item.wasteHandoverPlanId as Key)
      );
      navigate(`/WasteHandoverRegistration/Create`, { state: selectedRows });
    };

    // ==================== RENDER ====================
    return (
      <PageWrapper
        breadcrumb={[
          {
            text: t('waste-handover-plan.title'),
          },
        ]}
        extras={
          <ActionButtons
            isCreate={isCreate}
            isExport={isExport}
            isExportTemplate={isExportTemplate}
            isImport={isImport}
            isSendEmail={isSendEmail}
            exportLoading={exportLoading}
            exportTemplateLoading={exportTemplateLoading}
            exportEditTemplateLoading={exportEditTemplateLoading}
            importLoading={importLoading}
            sendEmailLoading={sendEmailLoading}
            onExportList={handleExportList}
            onExportTemplate={handleExportTemplate}
            onExportTemplateMenuClick={handleExportTemplateMenuClick}
            onImportChange={handleImportChange}
            onSendEmail={handleSendEmail}
            onShowFilter={showDrawer}
            isCreateVote={isCreateVote}
            onCreateVoucher={handleCreateVoucher}
            createVoucherDisabled={!selectedRowKeys.length}
            setOpenXacNhanImport={setOpenXacNhanImport}
            onOpenMultiCopy={handleEnterCopyMode}
            isCopyMode={isCopyMode}
            onConfirmCopyMode={handleConfirmCopyMode}
            onCancelCopyMode={handleCancelCopyMode}
            canConfirmCopyMode={copyRowKeys.length > 0}
          />
        }
      >
        {/* Table hiển thị danh sách */}
        <BoxContainer className="!gap-0">
          {/* Chú thích màu trạng thái */}
          <div className="flex items-center gap-6 mb-2 text-xs">
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-4 h-4 rounded-sm border border-gray-300"
                style={{ backgroundColor: '#ffffff' }}
              />
              <span>
                {t('waste-handover-plan.legend-not-sent-email')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-4 h-4 rounded-sm border border-gray-300"
                style={{ backgroundColor: '#E8F4FE' }}
              />
              <span>
                {t('waste-handover-plan.legend-sent-email')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-4 h-4 rounded-sm border border-gray-300"
                style={{ backgroundColor: 'rgb(180, 226, 185)' }}
              />
              <span>
                {t('waste-handover-plan.legend-created-slip')}
              </span>
            </div>
          </div>

          <style>{`
            .waste-handover-sent-email-row > td {
              background-color: #E8F4FE !important;
            }
            .waste-handover-sent-email-row:hover > td {
              background-color: #E8F4FE !important;
            }
            .waste-handover-created-slip-row > td {
              background-color: rgb(180, 226, 185) !important;
            }
            .waste-handover-created-slip-row:hover > td {
              background-color: rgb(180, 226, 185) !important;
            }

            /* Checkbox chế độ sao chép kế hoạch */
            .copy-mode-checkbox .ant-checkbox-inner {
              border-color: #16a34a !important;
            }
            .copy-mode-checkbox.ant-checkbox-checked .ant-checkbox-inner {
              background-color: #16a34a !important;
              border-color: #16a34a !important;
            }
          `}</style>

          {isCopyMode && (
            <div className="px-1 py-2 text-sm font-semibold text-red-600">
              Đang ở chế độ sao chép nhiều kế hoạch - Vui lòng chọn kế hoạch
            </div>
          )}
          <CustomProTable<WasteHandoverPlanModel>
            key={`table-${sourceData.length}`} // Force re-render khi sourceData thay đổi
            columns={isCopyMode ? columnsWithCopy : defaultColumn}
            rowKey="wasteHandoverPlanId"
            size="small"
            bordered
            loading={tableLoading}
            actionRef={tableRef}
            scroll={{ x: 'auto' }}
            tableAlertRender={false}
            onRow={(record) => {
              // Ưu tiên màu lá nếu đã tạo phiếu (ngay cả khi đã gửi mail)
              if (record.isRegistered === true) {
                return {
                  className: 'waste-handover-created-slip-row',
                  style: {
                    backgroundColor: 'rgb(180, 226, 185)',
                  },
                };
              }
              // Màu xanh da trời nếu đã gửi mail nhưng chưa tạo phiếu
              if (record.isSentEmail === true) {
                return {
                  className: 'waste-handover-sent-email-row',
                  style: {
                    backgroundColor: '#E8F4FE',
                  },
                };
              }
              return {};
            }}
            rowSelection={isCopyMode
              ? undefined
              : {
                columnWidth: 52, // mở rộng ô checkbox
                renderCell: (_value, _record, _index, originNode) => (
                  <div className="min-w-[40px] flex justify-center">{originNode}</div>
                ),
                getCheckboxProps: (record) => ({
                  disabled:
                    record.actived !== true ||
                    record.isRegistered === true ||
                    (selectionConstraint
                      ? !(
                        dayjs(record.deliveryDate).format('YYYY-MM-DD') ===
                        selectionConstraint.date &&
                        record.vendorNumber === selectionConstraint.vendorNumber &&
                        (record.wasteGroup || record.wasteGroupingContractName || '') ===
                        selectionConstraint.wasteGroup
                      )
                      : false), // Disable nếu không hoạt động, đã đăng ký (isRegistered === 1) hoặc không thoả constraint
                }),
                preserveSelectedRowKeys: true, // giữ lại các dòng đã chọn khi lọc
                selections: [
                  {
                    key: 'all',
                    text: 'Tất cả',
                    onSelect: () => {
                      // Không cần làm gì, ProTable sẽ tự xử lý
                    },
                  },
                  {
                    key: 'selected',
                    text: 'Đã chọn',
                    onSelect: () => {
                      // Không cần làm gì, ProTable sẽ tự xử lý
                    },
                  },
                  {
                    key: 'none',
                    text: 'Chưa chọn',
                    onSelect: () => {
                      // Không cần làm gì, ProTable sẽ tự xử lý
                    },
                  },
                ],
                onChange(selectedRowKeys, selectedRows) {
                  if (selectedRowKeys.length === 0) {
                    setSelectedRowKeys([]);
                    setSelectionConstraint(null);
                    return;
                  }

                  // Lấy constraint từ dòng đầu tiên được chọn
                  const first = selectedRows[0];
                  const ref = {
                    date: dayjs(first.deliveryDate).format('YYYY-MM-DD'),
                    vendorNumber: first.vendorNumber,
                    wasteGroup: first.wasteGroup || first.wasteGroupingContractName || '',
                  };

                  const isAllowed = (row: any) =>
                    dayjs(row.deliveryDate).format('YYYY-MM-DD') === ref.date &&
                    row.vendorNumber === ref.vendorNumber &&
                    (row.wasteGroup || row.wasteGroupingContractName || '') === ref.wasteGroup &&
                    row.actived === true;

                  const allowedKeys = sourceData
                    .filter(isAllowed)
                    .map((r) => r.wasteHandoverPlanId as Key);

                  const filteredKeys = selectedRowKeys.filter((k) => allowedKeys.includes(k));

                  setSelectionConstraint(ref);
                  setSelectedRowKeys(filteredKeys);
                },
                selectedRowKeys: selectedRowKeys,
              }}
            search={false}
            dataSource={sourceData}
            pagination={{
              ...tablePagination(dataSearch, setDataSearch),
              position: ['topRight'],
            }}
            options={false}
          />
        </BoxContainer>

        {/* Drawer tìm kiếm */}
        <SearchBox
          className="lg:!w-[60%]"
          footer={[
            <div key="footer" className="flex justify-end gap-3">
              <CustomButton
                onClick={() => {
                  handleResetFilters();
                  onCloseDrawer();
                }}
              >
                {t('btn.reset')}
              </CustomButton>
              <CustomButton
                type="primary"
                icon={<HiSearch />}
                htmlType="submit"
                onClick={() => {
                  form.submit();
                }}
              >
                {t('btn.search')}
              </CustomButton>
            </div>,
          ]}
          onClose={onCloseDrawer}
          open={openDrawer}
        >
          <WasteHandoverPlanSearchForm
            form={form}
            openDrawer={openDrawer}
            onClose={onCloseDrawer}
            onFinish={handleSearch}
            onReset={handleResetFilters}
            disableDeliveryDate={disableDeliveryDate}
            onDeliveryPresetChange={handleDeliveryPresetChange}
          />
        </SearchBox>
        <SendMailModal
          open={openSendMail}
          onClose={() => setOpenSendMail(false)}
          onSuccess={() => {
            // Load lại danh sách sau khi gửi mail thành công
            loadData(dataSearch.filters);
            tableRef.current?.reload();
          }}
        />


        <XacNhanImportModal handleImportChange={loadData} open={openXacNhanImport} onClose={() => setOpenXacNhanImport(false)} />
      </PageWrapper>
    );
  });
};

export default createWasteHandoverPlan;
